import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import Fire from '@images/icons/double-fire.svg';

import Modal from '../ProfileV2/shared/Modal';
import SelectField from '../ProfileV2/FormBuilder/SelectField';
import NoticeModal from '../ProfileV2/NoticeModal';
import Fetch from '@components/util/Fetch';
import { logCrmEvent } from '@javascripts/util/eventLogging';

import './styles.scss';

const languageMap = {
  en: 'English',
  fr: 'French',
};

const inverseLanguageMap = Object.fromEntries(
  Object.entries(languageMap).map((a) => a.reverse()),
);

const Terms = (props) => {
  const {
    data: {
      isCanadian,
      updatePath,
      initialTermsLanguage = 'en',
      terms,
      termsTitle,
      termsDisclaimer,
      termsMatchPrompt,
      unforced = true,
      hideUpdates = false,
      storedLocation = '',
    },
  } = props;

  const [language, setLanguage] = useState(initialTermsLanguage);
  const buttonRef = useRef(null);

  const multipleTerms = Object.keys(terms).length > 1;

  const close = () => {
    window.location = storedLocation;
  };

  const dismiss = () => {
    if (unforced) {
      close();
      logCrmEvent('disruptor_dismissed', { type: 'agent_terms' });
    }
  };

  const submit = async () => {
    buttonRef.current.classList.toggle('submitting');
    buttonRef.current.disabled = true;

    const payload = {
      terms_agree: true,
      legal_agreement_uuid: terms[language].uuid,
    };

    const resp = await Fetch.post({ url: updatePath, payload });

    await new Promise((resolve) => setTimeout(resolve, 2000));

    if (resp.error) {
      alert(resp.error);
    } else {
      logCrmEvent('disruptor_submitted', { type: 'agent_terms' });
      if (resp.redirectPath) {
        window.location = resp.redirectPath;
      } else {
        close();
        if (window.updateNotice) {
          window.updateNotice(
            {
              header: 'Success!',
              message: "You've accepted the ReferralExchange Agent Terms",
            },
            5000,
          );
        }
      }
    }

    if (!resp.redirectPath) {
      buttonRef.current.classList.toggle('submitting');
      buttonRef.current.disabled = false;
    }
  };

  const handleSelectChange = (e) => {
    const { value } = e.currentTarget.selectedOptions[0];
    setLanguage(inverseLanguageMap[value]);
  };

  const titleClass = ['terms-disclaimer'];

  if (multipleTerms) {
    titleClass.push('multiple-terms');
  }

  if (hideUpdates) {
    titleClass.push('hide-updates');
  }

  const modalContent = () => (
    <>
      <div className={titleClass.join(' ')}>
        <div className="highlighted-disclaimer-header">
          {
            termsMatchPrompt && (
              <div className="highlighted-prompt large-bold-font">
                <img src={Fire} alt="" />
                <span>
                  {termsMatchPrompt}
                </span>
              </div>
            )
          }
          <div className="disclaimer-header-container">
            <h2 className="headline-2 margin-bottom-8 margin-top-8 center" dangerouslySetInnerHTML={{ __html: termsTitle }} />
          </div>
        </div>
        {
          (multipleTerms || !hideUpdates) && (
            <div className="disclaimer-header">
              {multipleTerms && (
                <div>
                  <SelectField
                    id="languages"
                    label=" "
                    value={languageMap[language]}
                    selectOptions={Object.keys(terms).map((t) => languageMap[t])}
                    handleChange={handleSelectChange}
                    keyOnly
                  />
                </div>
              )}
              <div className="disclaimer">
                <p className="margin-bottom-8">{termsDisclaimer}</p>
                {
                  isCanadian
                    ? (
                      <ul>
                        <li>
                          <i className="check-circle" />
                          Implemented changes to comply with Canadian regulations, including individual provinces
                        </li>
                      </ul>
                    )
                    : (
                      <ul>
                        <li>
                          <i className="check-circle" />
                          You agree to only contact ReferralExchange clients using our app directly or manually
                          placed phone calls (unless you have independently obtained prior express written
                          consent to contact the client yourself)
                        </li>
                        <li>
                          <i className="check-circle" />
                          Other changes to better reflect our platform&apos;s ability to gather consent to contact
                          ReferralExchange&apos;s clients after the TCPA one to one consent changes.
                        </li>
                      </ul>
                    )
                }
              </div>
            </div>
          )
        }
        <div dangerouslySetInnerHTML={{ __html: terms[language].content }} />
      </div>
      <div className="agreement-actions">
        <div className="button-container">
          <button
            ref={buttonRef}
            type="submit"
            className="profile-edit-button large submit"
            onClick={submit}
          >
            <i className="fa-solid fa-spin spinner-third" />
            <i className="check-circle" />
            <span className="default-text">I agree to the ReferralExchange Agent Terms</span>
            <span className="submit-text">Submitting</span>
          </button>
          {
            unforced && (
              <button
                id="terms-close-button"
                type="submit"
                className="profile-edit-button large transparent"
                onClick={dismiss}
              >
                I&apos;ll do it later
              </button>
            )
          }
        </div>
      </div>
    </>
  );

  const termsClassList = [
    'edit-modal-inner',
    'terms-modal',
  ];

  return (
    <div>
      <Modal id="terms-modal" status="active" close={dismiss}>
        <div className={termsClassList.join(' ')}>{modalContent()}</div>
      </Modal>
      <NoticeModal />
    </div>
  );
};

export default Terms;

Terms.propTypes = {
  data: PropTypes.shape({
    initialTermsLanguage: PropTypes.string,
    isCanadian: PropTypes.bool.isRequired,
    hideUpdates: PropTypes.bool.isRequired,
    updatePath: PropTypes.string.isRequired,
    terms: PropTypes.objectOf(PropTypes.any).isRequired,
    termsTitle: PropTypes.string.isRequired,
    termsDisclaimer: PropTypes.string.isRequired,
    newAgentForcedTerms: PropTypes.bool,
    termsMatchPrompt: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.oneOf([null]),
    ]).isRequired,
    unforced: PropTypes.bool,
    storedLocation: PropTypes.string,
  }).isRequired,
};
